import React, { useCallback } from 'react'
import GiftReceiveResultPageView from 'src/pages/GiftCardPage/GiftReceiveResultPage/view'
import { GiftCardPageProps } from 'src/pages/GiftCardPage'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import {
  BannerSpace,
  CouponDetailInfoTabSpace,
  GiftDetailInfoByGiftIdxSpace,
  GiftHotDealListsSpace,
} from 'src/types/api'
import {
  couponDetailInfoTabQueryOption,
  giftCardBannerListQueryOption,
  giftHotDealListQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { maskPhoneNumber } from 'src/utils'
import { DateUtils } from 'src/utils/date'
import useHtml2CanvasDown from 'src/hooks/useHtml2CanvasDown.hook'
import { useModal } from 'src/provider/ModalContextProvider'
import GiftImgDownModal from 'src/components/common/Modal/children/GiftImgDownModal'

export interface GiftReceiveResultPageProps {
  giftDetailData: GiftDetailInfoByGiftIdxSpace.Response
  contentsStatus: GiftReceiveResultContentsStatus
  conponInfoList: ({
    label: string
    value: { text: string; bold?: boolean; color?: string }[]
  } | null)[]
  couponDetailTabData?: CouponDetailInfoTabSpace.Response
  Introduction?: string
  hotDealCouponList: GiftHotDealListsSpace.GiftHotDealItemType[]
  bannerItem: BannerSpace.BannerType
  handleImgDownLoad: () => void
  componentRef: React.RefObject<HTMLDivElement>
}

export interface GiftReceiveResultContentsStatus {
  title: string
  description: string
  couponTopBoxBlur: boolean
  couponBottomBox: { type: 'download' | 'used' | 'cantUse'; text?: string; subText?: string }
  message: boolean
  couponInfo: boolean
  refundBtn: boolean
  sendTooBtn: boolean
  statusName: string
}

const GiftReceiveResultPage = ({ giftDetailData }: GiftCardPageProps) => {
  const {
    PNumber,
    CouponOrderID,
    PinNumber,
    ExpireDT,
    PurchaseDT,
    RegularPrice,
    Introduction,
    ValidDay,
    CouponType,
    CouponNumber,
    GiftStatus,
  } = giftDetailData
  const { handleDownloadClick, componentRef } = useHtml2CanvasDown()
  const { openModal } = useModal()
  const { DateFormat, isValidInYears } = DateUtils

  // const isAlwaysCoupon = CouponType === 0
  const isCancelAble = isValidInYears(PurchaseDT, 5)

  const {
    data: hotDealItemsQueryData = { Body: { HotdealCouponByType: Array(3).fill({}) } },
    isLoading: isHotDealItemsLoading,
  } = useCustomQuery<GiftHotDealListsSpace.Response>(
    giftHotDealListQueryOption({ Type: 2, Offset: '0', Length: '6' }),
  )

  const {
    data: bannerListQueryData = { Body: { banner: Array(3).fill({}) } },
    isLoading: isBannerListLoading,
  } = useCustomQuery<BannerSpace.Response>(giftCardBannerListQueryOption({ Type: 1 }))

  const {
    data: couponDetailInfoTabQueryData,
    isSuccess: isCouponDetailInfoSuccess,
    isLoading: isCouponDetailInfoLoading,
  } = useCustomQuery<CouponDetailInfoTabSpace.Response>(
    couponDetailInfoTabQueryOption({
      CouponType: Number(CouponType) as 0 | 1,
      CouponNumber: CouponNumber,
    }),
  )

  const contentsStatusList: { [key: string]: GiftReceiveResultContentsStatus } = {
    // 사용가능
    1: {
      title: '쿠폰 선물을 받았습니다.',
      description: '유효기간 안에 사용해주세요.',
      couponTopBoxBlur: false,
      couponBottomBox: { type: 'download' },
      message: true,
      couponInfo: true,
      refundBtn: isCancelAble,
      sendTooBtn: true,
      statusName: '사용 가능',
    },
    // 사용완료
    2: {
      title: '쿠폰 선물을 받았습니다.',
      description: '',
      couponTopBoxBlur: true,
      couponBottomBox: { type: 'used' },
      message: true,
      couponInfo: true,
      refundBtn: false,
      sendTooBtn: true,
      statusName: '사용 완료',
    },
    // 거절/미수신
    3: {
      title: `${PNumber?.slice(-4)}님이 보낸 쿠폰 선물을\n거절했습니다.`,
      description: '선물 받기 유효기간이 만료된 경우에도\n선물이 거절되어 친구에게 돌아갑니다.',
      couponTopBoxBlur: true,
      couponBottomBox: {
        type: 'cantUse',
        text: '거절한 선물',
        subText: '친구에게 돌려준 선물입니다.',
      },
      message: true,
      couponInfo: false,
      refundBtn: false,
      sendTooBtn: false,
      statusName: '',
    },
    // 취소완료
    4: {
      title: `${PNumber?.slice(-4)}님이 선물을\n취소했습니다.`,
      description: '',
      couponTopBoxBlur: true,
      couponBottomBox: {
        type: 'cantUse',
        text: '취소된 선물',
        subText: '구매자가 취소한 선물입니다.',
      },
      message: false,
      couponInfo: false,
      refundBtn: false,
      sendTooBtn: false,
      statusName: '',
    },
    // 환불접수완료
    5: {
      title: '쿠폰 선물을 받았습니다.',
      description: '',
      couponTopBoxBlur: true,
      couponBottomBox: { type: 'cantUse', text: '환불한 선물', subText: '환불접수된 선물입니다.' },
      message: true,
      couponInfo: true,
      refundBtn: false,
      sendTooBtn: true,
      statusName: '환불 접수 완료',
    },
    // 환불완료
    6: {
      title: '쿠폰 선물을 받았습니다.',
      description: '',
      couponTopBoxBlur: true,
      couponBottomBox: { type: 'cantUse', text: '환불한 선물', subText: '환불처리된 선물입니다.' },
      message: true,
      couponInfo: true,
      refundBtn: false,
      sendTooBtn: true,
      statusName: '환불 완료',
    },
    // 기간만료
    7: {
      title: '쿠폰 선물을 받았습니다.',
      description: '',
      couponTopBoxBlur: true,
      couponBottomBox: {
        type: 'cantUse',
        text: '기간 만료',
        subText: '유효기간이 만료되어 사용할 수 없습니다.',
      },
      message: true,
      couponInfo: true,
      refundBtn: isCancelAble,
      sendTooBtn: true,
      statusName: '기간 만료',
    },
  }

  const contentsStatus = contentsStatusList[`${GiftStatus}`]

  const conponInfoList = [
    { label: '쿠폰 상태', value: [{ text: contentsStatus.statusName }] },
    ...(PinNumber?.trim() ? [{ label: '쿠폰 인증번호', value: [{ text: PinNumber.trim() }] }] : []),
    [1, 2, 7].includes(GiftStatus)
      ? { label: '유효 기간', value: [{ text: DateFormat(ExpireDT, 'YY.MM.DD') }] }
      : null,
    { label: '주문 번호', value: [{ text: CouponOrderID }] },
    { label: '상품 금액', value: [{ text: `${RegularPrice?.toLocaleString()}원` }] },
    { label: '선물한 사람', value: [{ text: maskPhoneNumber(PNumber) }] },
  ]

  const setCouponDetailInfoItems = useCallback(() => {
    return {
      ...couponDetailInfoTabQueryData?.Body,
      CouponInfo: {
        Title: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Title,
        Contents: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Contents?.replace(
          '{{ValidDay}}',
          `${ValidDay}일`,
        ),
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponDetailInfoSuccess, couponDetailInfoTabQueryData])

  const handleImgDownLoad = () =>
    handleDownloadClick(() => {
      openModal({ type: 'custom', contents: { children: <GiftImgDownModal /> } })
    })

  const props: GiftReceiveResultPageProps = {
    giftDetailData,
    contentsStatus,
    couponDetailTabData: setCouponDetailInfoItems(),
    conponInfoList,
    Introduction,
    hotDealCouponList: hotDealItemsQueryData?.Body?.HotdealCouponByType,
    bannerItem: bannerListQueryData?.Body?.banner?.[0],
    handleImgDownLoad,
    componentRef,
  }

  return (
    <React.Fragment>
      <RenderIf
        when={
          !giftDetailData ||
          isCouponDetailInfoLoading ||
          isHotDealItemsLoading ||
          isBannerListLoading
        }
      >
        <Loading.Default />
      </RenderIf>
      <GiftReceiveResultPageView {...props} />
    </React.Fragment>
  )
}
export default GiftReceiveResultPage
